'use client';

import { useState } from 'react';
import { Box, CircularProgress, Fade, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';
import { UserSection as StyledUserSection } from '../StyledComponents';
import { useAuth } from '../../../hooks/useAuth';
import UserProfile from './UserProfile';
import { useMsal } from '@azure/msal-react';

function SimpleLoginButton({ onLogin }: { onLogin: () => void }) {
  return (
    <Box
      component="button"
      onClick={onLogin}
      sx={{
        padding: '8px 16px',
        background: 'rgba(255,255,255,0.2)',
        border: '1px solid rgba(255,255,255,0.3)',
        borderRadius: 2,
        color: 'white',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: 500,
        transition: 'all 0.2s ease',
        '&:hover': {
          background: 'rgba(255,255,255,0.3)',
        }
      }}
    >
      Sign In
    </Box>
  );
}

export default function UserSection() {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const auth = useAuth();
  const msal = useMsal();
  const { isAuthenticated, user, provider, isLoading, login, logout } = auth;

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    setShowLogoutConfirm(true);
  };

  const handleLogoutConfirm = async () => {
    setShowLogoutConfirm(false);
    await logout();
    if (provider === 'azure-ad' && msal.accounts && msal.accounts.length > 0) {
      await msal.instance.logoutRedirect({
        postLogoutRedirectUri: '/'
      });
    }
    setAnchorEl(null);
  };

  const handleLogoutCancel = () => {
    setShowLogoutConfirm(false);
  };

  if (isLoading) {
    return (
      <StyledUserSection>
        <CircularProgress size={24} sx={{ color: 'white' }} />
      </StyledUserSection>
    );
  }

  return (
    <StyledUserSection>
      <Fade in={true} timeout={500}>
        <Box>
          {isAuthenticated && user ? (
            <>
              <UserProfile
                user={user}
                anchorEl={anchorEl}
                onMenuOpen={handleMenuOpen}
                onMenuClose={handleMenuClose}
                onLogout={handleLogout}
              />
              <Dialog open={showLogoutConfirm} onClose={handleLogoutCancel}>
                <DialogTitle>Confirm Logout</DialogTitle>
                <DialogContent>
                  <DialogContentText>
                    Are you sure you want to log out?
                  </DialogContentText>
                </DialogContent>
                <DialogActions>
                  <Button onClick={handleLogoutCancel} color="primary" variant='outlined' autoFocus>
                    Cancel
                  </Button>
                  <Button onClick={handleLogoutConfirm} color="primary">
                    Log Out
                  </Button>
                </DialogActions>
              </Dialog>
            </>
          ) : (
            <SimpleLoginButton onLogin={() => login()} />
          )}
        </Box>
      </Fade>
    </StyledUserSection>
  );
}